import dayjs from "dayjs";

import { formatCurrency } from "@/helpers/currency-helpers";
import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import { CompanyPaymentsMessages } from "@/messages/customer/company-payments.messages";
import { CustomerSalesProductPaymentStatus } from "@/types/customer-sales-product-payment-status.types";

type StatusChipColor = "default" | "warning" | "info" | "success" | "error";

type Return = Partial<ICustomerSalesProductPayment> & {
  formattedDueDate: string;
  formattedValue: string;
  formattedPaymentDate: string;
  statusLabel: string;
  statusChipColor: StatusChipColor;
};

type Props = {
  payment: ICustomerSalesProductPayment | null;
  messages: CompanyPaymentsMessages;
};

function useCompanyPaymentData({ payment, messages }: Props): Return {
  const formatDate = (date?: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "-");

  const getStatusLabel = (status?: CustomerSalesProductPaymentStatus): string => {
    switch (status) {
      case CustomerSalesProductPaymentStatus.New:
        return messages.newStatusMessage;
      case CustomerSalesProductPaymentStatus.BillingSent:
        return messages.billingSentStatusMessage;
      case CustomerSalesProductPaymentStatus.Paid:
        return messages.paidStatusMessage;
      case CustomerSalesProductPaymentStatus.Cancelled:
        return messages.cancelledStatusMessage;
      case CustomerSalesProductPaymentStatus.Delayed:
        return messages.delayedStatusMessage;
      default:
        return "-";
    }
  };

  const getStatusChipColor = (status?: CustomerSalesProductPaymentStatus): StatusChipColor => {
    switch (status) {
      case CustomerSalesProductPaymentStatus.New:
        return "warning";
      case CustomerSalesProductPaymentStatus.BillingSent:
        return "info";
      case CustomerSalesProductPaymentStatus.Paid:
        return "success";
      case CustomerSalesProductPaymentStatus.Cancelled:
        return "error";
      case CustomerSalesProductPaymentStatus.Delayed:
        return "warning";
      default:
        return "default";
    }
  };

  return {
    ...payment,
    formattedDueDate: formatDate(payment?.dueDate),
    formattedValue: formatCurrency(payment?.value || 0),
    formattedPaymentDate: formatDate(payment?.paymentDate),
    statusLabel: getStatusLabel(payment?.status),
    statusChipColor: getStatusChipColor(payment?.status),
  };
}

export default useCompanyPaymentData;
